"use client"

import { useEffect, useState, type CSSProperties } from "react"
import { getIstanbulCountdown } from "@lib/util/istanbul-countdown"
import type { PublicStorefrontSettings } from "@modules/global-shell/types"
import styles from "./countdown-campaign.module.css"

const PETALS = Array.from({ length: 12 }, (_, index) => index)
const zero = { days: 0, hours: 0, minutes: 0, seconds: 0 }
const two = (value: number) => String(value).padStart(2, "0")

const CountdownCampaign = ({ settings }: { settings: PublicStorefrontSettings["countdown"] }) => {
  const [dismissed, setDismissed] = useState(false)
  const [timeLeft, setTimeLeft] = useState(zero)
  const key = `vetpets-countdown-dismissed:${settings.dismissal_fingerprint}`

  useEffect(() => {
    if (!settings.enabled) return
    setDismissed(window.sessionStorage.getItem(key) === "true")
    const update = () => setTimeLeft(getIstanbulCountdown(new Date()))
    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [key, settings.enabled])

  if (!settings.enabled || dismissed) return null
  const units: Array<[string, number]> = [
    ["Gün", timeLeft.days],
    ["Saat", timeLeft.hours],
    ["Dakika", timeLeft.minutes],
    ["Saniye", timeLeft.seconds],
  ]

  return (
    <section className={styles.countdown} aria-label={settings.title} data-testid="global-countdown">
      <div className={styles.countdown__petals} aria-hidden="true">
        {PETALS.map((index) => (
          <span
            key={index}
            className={styles.countdown__petal}
            style={{ "--petal-index": index } as CSSProperties}
          />
        ))}
      </div>
      <div className={styles.countdown__inner}>
        <div className={styles.countdown__copy}>
          <h2 className={styles.countdown__title}>{settings.title}</h2>
          <p className={styles.countdown__subtitle}>{settings.subtitle}</p>
        </div>
        <div className={styles.countdown__timer} aria-live="polite">
          {units.map(([label, value]) => (
            <div className={styles.countdown__card} key={label}>
              <strong className={styles.countdown__value}>{two(value)}</strong>
              <span className={styles.countdown__label}>{label}</span>
            </div>
          ))}
        </div>
        <button
          className={styles.countdown__close}
          type="button"
          aria-label="Kampanyayı kapat"
          onClick={() => {
            window.sessionStorage.setItem(key, "true")
            setDismissed(true)
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {settings.shipping_text.trim() ? (
        <div className={styles.countdown__shipping}>{settings.shipping_text}</div>
      ) : null}
    </section>
  )
}

export default CountdownCampaign
